import Model, { attr } from '@warp-drive/legacy/model';
import type { Type } from '@warp-drive/core/types/symbols';

// Proto: auth.Client.
export default class Client extends Model {
  declare [Type]: 'client';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // allowedAudiences contains a list of Audiences that the client has been
  // given rights to access.
  @attr('array-string', { defaultValue: newArray })
  declare allowedAudiences: unknown;

  // allowedRegions contains a list of regions that the client has been
  // given permission to access. This enables filtering for clients based on
  // geographic region.
  @attr('array-string', { defaultValue: newArray })
  declare allowedRegions: unknown;

  // allowedTenantAccess contains a list of Tenants that the client has been
  // given rights to access.
  @attr('array-string', { defaultValue: newArray })
  declare allowedTenantAccess: unknown;

  // grantTypes contains a list of grant types the client is allowed to use.
  //
  // Pattern: client_credentials|authorize_code|implicit|refresh_token
  @attr('array-string', { defaultValue: newArray })
  declare grantTypes: unknown;

  // responseTypes contains a list of the OAuth 2.0 response type strings
  // that the client can use at the authorization endpoint.
  //
  // Pattern: id_token|code|token
  @attr('array-string', { defaultValue: newArray })
  declare responseTypes: unknown;

  // scopes contains a list of values the client is entitled to use when
  // requesting an access token (as described in Section 3.3 of OAuth 2.0
  // [RFC6749]).
  //
  // Pattern: ([a-zA-Z0-9\.]+\s)+
  @attr('array-string', { defaultValue: newArray })
  declare scopes: unknown;

  // public is a boolean that identifies this client as public, meaning that
  // it does not have a secret. It will disable the client_credentials grant
  // type for this client if set.
  @attr('boolean', { defaultValue: false })
  declare public: boolean;

  // disabled stops the client from being able to authenticate to the system.
  @attr('boolean', { defaultValue: false })
  declare disabled: boolean;

  // name contains a human-readable string name of the client to be presented
  // to the end-user during authorization.
  @attr('string', { defaultValue: '' })
  declare name: string;

  // secret is the client's secret. The secret will be included in the create
  // request as cleartext, and then never again. The secret is stored using
  // Argon2id so it is impossible to recover it.
  // Tell your users that they need to remember the client secret as it will
  // only be returned once, never to be seen again.
  @attr('string', { defaultValue: '' })
  declare secret: string;

  // RedirectUris contains a list of allowed redirect urls for the client, for
  // example: https://example.com/oauth/callback.
  @attr('array-string', { defaultValue: newArray })
  declare redirectUris: unknown;

  // owner identifies the owner of the OAuth 2.0 Client.
  @attr('string', { defaultValue: '' })
  declare owner: string;

  // policyURI allows the application developer to provide a URI string that
  // points to a human-readable privacy policy document that describes how the
  // deployment organization collects, uses, retains, and discloses personal
  // data.
  @attr('string', { defaultValue: '' })
  declare policyUri: string;

  // termsOfServiceUri allows the application developer to provide a URI
  // string that points to a human-readable terms of service document that
  // describes and outlines the contractual relationship between the end-user
  // and the client application that the end-user accepts when authorizing
  // their use of the client.
  @attr('string', { defaultValue: '' })
  declare termsOfServiceUri: string;

  // clientUri allows the application developer to provide a URI string that
  // points to a human-readable web page that provides information about the
  // client application.
  // If present, the server SHOULD display this URL to the end-user in a
  // click-able fashion.
  @attr('string', { defaultValue: '' })
  declare clientUri: string;

  // logoUri is an URL string that references a logo for the client.
  @attr('string', { defaultValue: '' })
  declare logoUri: string;

  // contacts contains a list ways to contact the developers responsible for
  // this OAuth 2.0 client, typically email addresses.
  @attr('array-string', { defaultValue: newArray })
  declare contacts: unknown;

  // published provides a switch to hide specific clients if not quite ready
  // for the prime time, or if wanting to keep them hidden.
  @attr('boolean', { defaultValue: false })
  declare published: boolean;
}

// newArray returns a new empty array.
function newArray() {
  return [];
}
